import { hidden, normalizeRoutes, oneOf, route } from "./definition";

const WORKFLOW_PARAMS =
  "?branch=:branchId&commit=:commitId&step=:step&file=:file&connectingClaimId=:connectingClaimId&execution=:execution&run=:run&stepTab=:stepTab&showRuns=:showRuns&view=:view&tab=:tab&browserStep=:browserStep&stepTest=:stepTest&linkType=:linkType=webpage&map=:map&density=:density&spacing=:spacing&connectors=:connectors&chat=:chatId&navigation=:navigation";

const SORT_PARAMS = "sort=:sort&dir=:dir";

const SCOPE_PARAM = "scope=:scope=both";

const FEED_FILTERS = `status=:status=all&space=:space=all&app=:app=all&creator=:creator=all&${SORT_PARAMS}`;
const FEED_FILTERS_WITH_TRIGGER = `${FEED_FILTERS}&trigger=:trigger=all`;

const PAGE_FILTERS = "?type=:type=webpage&sort=:sort";

export const routes = normalizeRoutes({
  root: route("/", {
    modal: oneOf({
      space: "id=:id&tab=:tab=settings",
      group: "id=:id&tab=:tab=settings",
      member: "id=:id&tab=:tab=membership",
      role: "id=:id",
      serviceAccount: "id=:id&tab=:tab=settings",
      network: "id=:id&tab=:tab=settings",
      aiProvider: "id=:id&tab=:tab=settings",
      admins: "",
      customizeNav: "",
      connector: "id=:id&tab=:tab=settings",
      connectorTest: "id=:id",
      storage:
        "spaceId=:spaceId&branchId=:branchId&workflowId=:workflowId&volume=:volume&section=:section",
      skill: "id=:id&tab=:tab=settings&file=:file=SKILL.md",
      workflowValue: "id=:id",
      storageUsage: "path=:path&dir=:dir",
      createSpace: "workflowId=:workflowId",
    }),
    index: route("/"),
    recents: route(`/recents?${FEED_FILTERS_WITH_TRIGGER}`, {
      autofix: route("/autofix"),
      autotune: route("/autotune"),
    }),
    favorites: route(`/favorites?${FEED_FILTERS_WITH_TRIGGER}`, {
      links: route(`/links${PAGE_FILTERS}`),
    }),
    links: route(
      `/links${PAGE_FILTERS}&space=:space=all&app=:app=all&creator=:creator=all&q=:q&otherSpaces=:otherSpaces=0`
    ),
    personal: route(`/personal?${SORT_PARAMS}`, {
      links: route(`/links${PAGE_FILTERS}`),
      workflow: route(`/workflows/:workflowId${WORKFLOW_PARAMS}`),
    }),
    galleryImport: hidden(
      route("/gallery/import/:workflowId/:commitId/:sha256")
    ),
    chats: route(
      `/chats?tab=:tabId&q=:q&userId=:userId&history=:history&monitoring=:monitoring&workflowList=:workflowList=recents&${FEED_FILTERS_WITH_TRIGGER}`
    ),
    space: route(`/spaces/:spaceId?${FEED_FILTERS_WITH_TRIGGER}`, {
      links: route(`/links${PAGE_FILTERS}`),
      leaderboard: route("/leaderboard?sort=:sort=name&dir=:dir=asc"),
      workflow: route(`/workflows/:workflowId${WORKFLOW_PARAMS}`),
    }),
    connectors: route("/connectors", {
      connected: route(
        `/connected?q=:q&status=:status=all&type=:type=all&creator=:creator=all&space=:space=all&${SORT_PARAMS}`
      ),
      browse: route("/browse?q=:q&tag=:tag=all&justCreated=:justCreated", {
        create: route("/create?identifier=:identifier"),
      }),
      legacyDirectory: hidden(
        route("/directory", { create: route("/create") })
      ),
      legacyLibrary: hidden(route("/library", { create: route("/create") })),
    }),
    skills: route("/skills", {
      list: route(
        `/list?q=:q&creator=:creator=all&space=:space=all&${SORT_PARAMS}`
      ),
    }),
    // scope is declared once here so it folds onto every child tab and persists
    // across tab navigation automatically (see mergeTails / ownTail in ./definition).
    monitoring: route(`/monitoring?${SCOPE_PARAM}`, {
      value: route(
        "/value?window=:window=30&space=:space&sort=:sort=total&dir=:dir"
      ),
      workflows: route(
        "/workflows?sort=:sort&panel=:panel&space=:space&pspace=:pspace&auth=:auth&view=:view=packages&search=:search"
      ),
      access: route(
        "/access?node=:node&search=:search&collapse=:collapse&hideDirect=:hideDirect&hideInherited=:hideInherited&hideAvailable=:hideAvailable&connectors=:connectors=loose"
      ),
      ai: route(
        "/ai?provider=:provider&view=:view=models&window=:window=30&space=:space&sort=:sort=cost&dir=:dir"
      ),
      connectors: route(
        "/connectors?window=:window=30&space=:space&connector=:connector&sort=:sort=requests&dir=:dir&workflowSort=:workflowSort=requests&workflowDir=:workflowDir"
      ),
      requests: route(`/requests?q=:q&requester=:requester=all&${SORT_PARAMS}`),
    }),
    settings: route("/settings", {
      preferences: route("/preferences"),
      connectedAccounts: hidden(route("/connected-accounts")),
      mcp: route("/mcp"),
      spaces: route(
        `/spaces?q=:q&show=:show=all&manager=:manager=all&member=:member=all&${SORT_PARAMS}`,
        {
          new: route("/new"),
          space: hidden(route("/:spaceId")),
        }
      ),
      members: route("/members", {
        users: route(
          `/users?q=:q&show=:show=active&group=:group=all&${SORT_PARAMS}`,
          {
            invite: route("/invite"),
          }
        ),
        serviceAccounts: route(`/service-accounts?q=:q&${SORT_PARAMS}`, {
          device: hidden(route("/device?user_code=:userCode")),
        }),
      }),
      userGroups: route(`/user-groups?q=:q&${SORT_PARAMS}`, {
        new: route("/new"),
      }),
      apiKeys: route(`/api-keys?${SORT_PARAMS}`),
      sso: route("/sso?provider=:provider&tab=:tab=settings", {
        new: route("/new?idp=:idp=other"),
      }),
      security: route("/security"),
      tenant: route("/tenant"),
      company: hidden(route("/company")),
      customDomains: route("/custom-domains?domain=:domain"),
      ai: route("/ai?configId=:configId&tab=:tab=settings", {
        new: route("/new?tab=:tab=settings&provider=:provider"),
      }),
      roles: route("/roles?q=:q&scope=:scope=space&sort=:sort=name", {
        new: route("/new?scopeKind=:scopeKind"),
      }),
      auditLogs: route("/audit-logs", {
        logs: route(
          "/logs?q=:q&status=:status=all&source=:source&user=:user=all&window=:window=all"
        ),
        destinations: route("/destinations?destinationId=:destinationId", {
          new: route("/new"),
        }),
      }),
      featureFlags: route(`/feature-flags?${SORT_PARAMS}`),
      networking: route("/networking", {
        new: route("/new?tab=:tab=settings"),
        rules: route("/rules?ruleId=:ruleId", {
          new: route("/new"),
        }),
      }),
    }),
    watchtower: route("/watchtower"),
    version: route("/version"),
    credits: route("/credits"),
    authError: hidden(
      route("/auth/error?error=:error&error_description=:error_description")
    ),
  }),
} as const).root;
